import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Subscription } from 'rxjs';
import { CognitoService } from 'src/app/services/cognito.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { EditorIframeComponent } from '../editor-iframe/editor-iframe.component';
import { ActivatedRoute, Router } from '@angular/router';
import { PersonaReviewComponent } from '../persona-review/persona-review.component';
import { EditComponent } from '@workspaces/utils/edit-component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { workspaceAgentFeature, WorkspaceAgentFetch } from '@workspaces/store/agent-status.feature';
import { AppState } from 'src/app/state/app.state';
import { WorkspaceStatus } from '@workspaces/models/agent';
import { AiDraftingProgressComponent } from '../ai-drafting-progress/ai-drafting-progress.component';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { CollabraService } from '@workspaces/utils/collabra.service';
import { CommonModule } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-edit-document',
  templateUrl: './edit-document.component.html',
  styleUrls: ['./edit-document.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDividerModule,
    MatIconModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatCardModule,
    EditorIframeComponent,
    PersonaReviewComponent,
    AiDraftingProgressComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatMenuModule,
    MatProgressSpinner
  ]
})
export class EditDocumentComponent extends EditComponent implements OnInit, OnDestroy {
  public readonly COLLABORA_URL = 'https://d3teh9i6vsekhp.cloudfront.net/browser/c042625181/cool.html';
  public readonly DRIFT_API = 'http://internal-drift-ss-dev-ecs-alb-1128816714.eu-central-1.elb.amazonaws.com';
  public readonly DOCUMENT_ID = '37151b4c-c6f3-49a1-a539-b583293b5b7e';
  private readonly sub = new Subscription();
  public editorFormControls = {
    collaboraUrl: new FormControl(this.COLLABORA_URL),
    driftApi: new FormControl(this.DRIFT_API),
    documentId: new FormControl(this.DOCUMENT_ID),
    useConfigForDriftApi: new FormControl(false),
    accesstoken: new FormControl('No access token'),
    useConfigForAccessToken: new FormControl(false)
  };
  public editorForm = new FormGroup(this.editorFormControls);
  public generatedWopiUrl = '';
  public generatedEditorSrc = '';
  public isDebugMode = false;
  public editorIsOpen = false;
  public documentStatus: WorkspaceStatus = { status: 'STATUS_NOT_FOUND', workspace_id: '' };

  constructor(
    public readonly cognitoService: CognitoService,
    private readonly config: ConfigurationService,
    protected override readonly route: ActivatedRoute,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly store: Store<AppState>,
    public readonly collabraService: CollabraService
  ) {
    super(route, snackbar, router);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.editorFormControls.documentId.patchValue(this.workspaceId);
    this.sub.add(
      this.editorFormControls.useConfigForAccessToken.valueChanges.subscribe(value => {
        if (value) {
          this.editorFormControls.accesstoken.disable();
        } else {
          this.editorFormControls.accesstoken.enable();
        }
      })
    );
    this.sub.add(
      this.editorFormControls.useConfigForDriftApi.valueChanges.subscribe(value => {
        if (value) {
          this.editorFormControls.driftApi.disable();
        } else {
          this.editorFormControls.driftApi.enable();
        }
      })
    );
    this.checkDebugParam();
    this.onUpdate();
    this.sub.add(
      this.store.select(workspaceAgentFeature.workspaceAgentData(this.workspaceId)).subscribe(status => {
        if (status && status.workspace_id === this.workspaceId) {
          this.documentStatus = status;
        }
      })
    );
    this.store.dispatch(WorkspaceAgentFetch({ workspaceId: this.workspaceId }));
  }

  checkDebugParam(): void {
    this.sub.add(
      this.route.queryParams.subscribe(params => {
        const debugValue = params['debug'];

        this.isDebugMode = debugValue && (debugValue.toLowerCase() === 'true' || debugValue === '1');
        if (!this.isDebugMode) {
          this.editorIsOpen = true;
        }
      })
    );
  }

  onUpdate() {
    const usedMockDriftApiUrl = this.isDebugMode && !this.editorFormControls.useConfigForDriftApi.value;
    const driftApi = usedMockDriftApiUrl
      ? (this.editorFormControls.driftApi.value ?? this.config.getConfiguration().driftApi)
      : this.config.getConfiguration().driftApi;
    const accessToken = this.cognitoService.authenticationSubject.getValue()?.getIdToken().getJwtToken();

    this.generatedWopiUrl = `${driftApi}/wopi/files/${this.editorFormControls.documentId.value}`;
    let usedCollabora = this.COLLABORA_URL;
    if (!this.isDebugMode) {
      usedCollabora = location.origin + '/browser/c042625181/cool.html';
    } else {
      usedCollabora = this.editorFormControls.collaboraUrl.value ?? '';
    }
    this.generatedEditorSrc = `${usedCollabora}?WOPISrc=${encodeURIComponent(this.generatedWopiUrl)}&access_token=${accessToken}&search=1.2|section`;
  }

  public childWindow: Window | null = null;
  private childUrl: string = window.origin + '/opener';

  openEditorInNewWindow(): void {
    if (this.childWindow && !this.childWindow.closed) {
      this.childWindow.focus();
      return;
    }
    const windowFeatures =
      'width=' +
      screen.width +
      ',height=' +
      screen.height +
      ',resizable=yes' +
      ',scrollbars=yes' +
      ',toolbar=no' +
      ',menubar=no' +
      ',location=no' +
      ',status=no';

    this.childWindow = window.open(this.childUrl, 'EditorWindow', windowFeatures);
    if (this.childWindow) {
      this.setupChildMessageListener();
      const checkClose = setInterval(() => {
        if (this.childWindow && this.childWindow.closed) {
          this.closeChildWindow();
          clearInterval(checkClose);
        }
      }, 1000);
    }
  }

  private setupChildMessageListener(): void {
    window.addEventListener('message', event => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const data = event.data;

      if (data && data.type === 'CHILD_READY' && event.source === this.childWindow) {
        this.childWindow?.postMessage(
          {
            type: 'SEND_IFRAME_URL',
            src: { collaboraIframeUrl: this.generatedEditorSrc, workspaceId: this.workspaceId }
          },
          window.location.origin
        );
      }
    });
  }

  openEditorHere() {
    if (this.generatedEditorSrc) {
      this.editorIsOpen = true;
    }
  }

  closeChildWindow(): void {
    if (this.childWindow) {
      this.childWindow.close();
      this.childWindow = null;
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.closeChildWindow();
  }
}
